import React from 'react';
import { Save } from 'lucide-react';
import Modal from '../Modal';

interface Course {
  id: string;
  title: string;
  description: string;
  full_price: number;
  discount_price?: number;
  category: string;
  is_active: boolean;
  hidden: boolean;
  duration_hours?: number;
  skill_level?: string;
  thumbnail_url?: string;
  created_at: string;
  updated_at: string;
}

interface Bundle {
  id: string;
  title: string;
  description: string;
  full_price: number;
  discount_price?: number;
  course_ids: string[];
  is_active: boolean;
  hidden: boolean;
  thumbnail_url?: string;
  savings_percentage?: number;
  created_at: string;
  updated_at: string;
}

interface BundleFormData {
  title: string;
  description: string;
  full_price: string;
  discount_price: string;
  course_ids: string[];
  is_active: boolean;
  hidden: boolean;
  thumbnail_url: string;
  savings_percentage: string;
}

interface BundleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  formData: BundleFormData;
  setFormData: React.Dispatch<React.SetStateAction<BundleFormData>>;
  editingBundle: Bundle | null;
  courses: Course[];
  onToggleCourse: (courseId: string) => void;
}

export default function BundleFormModal({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  editingBundle,
  courses,
  onToggleCourse
}: BundleFormModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingBundle ? 'Edit Bundle' : 'Create New Bundle'}
    >
      <form onSubmit={onSubmit}>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Savings Percentage
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.savings_percentage}
              onChange={(e) => setFormData({ ...formData, savings_percentage: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white h-24"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Full Price ($) *
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.full_price}
              onChange={(e) => setFormData({ ...formData, full_price: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Discount Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.discount_price}
              onChange={(e) => setFormData({ ...formData, discount_price: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Thumbnail URL
            </label>
            <input
              type="url"
              value={formData.thumbnail_url}
              onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:border-purple-500 text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Courses *
            </label>
            <div className="bg-white/5 border border-white/20 rounded-lg p-4 max-h-60 overflow-y-auto">
              {courses.length === 0 ? (
                <p className="text-gray-400 text-sm">No courses available. Please create courses first.</p>
              ) : (
                <div className="space-y-2">
                  {courses.map((course) => (
                    <label key={course.id} className="flex items-center space-x-3 p-2 hover:bg-white/5 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.course_ids.includes(course.id)}
                        onChange={() => onToggleCourse(course.id)}
                        className="w-5 h-5 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-white font-medium">{course.title}</div>
                        <div className="text-gray-400 text-sm">${course.discount_price || course.full_price}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-2">Selected: {formData.course_ids.length} course(s)</p>
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="text-sm text-gray-300">Active (available for purchase)</span>
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.hidden}
                onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })}
                className="w-5 h-5 rounded"
              />
              <span className="text-sm text-gray-300">Hide from customers</span>
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-white"
          >
            <Save className="w-5 h-5" />
            <span>{editingBundle ? 'Update' : 'Create'} Bundle</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}
